import {
  ActionIcon,
  Button,
  CopyButton,
  Divider,
  Drawer,
  Group,
  Paper,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  IconCalendar,
  IconCopy,
  IconDownload,
  IconFileInvoice,
  IconReceipt,
  IconWallet,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import StatusText from "../../../../common/components/DataTable/StatusText";
import classes from "./PaymentDrawer.module.css";

const currencySymbol = { INR: "₹", USD: "$", EUR: "€", NPR: "NRs" };

const formatPaymentDateTime = (value) => {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD MMM YYYY [at] h:mm A") : "—";
};

const formatMethodLabel = (method) => {
  if (!method) return "—";
  return String(method)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const paidViaLabel = (method) => {
  const label = formatMethodLabel(method);
  return label === "—" ? "Payment received" : `Paid via ${label}`;
};

const formatAmountLine = (payment) => {
  const currency = payment?.currency || "INR";
  const amount = Number(payment?.total_amount) || 0;
  const sym = currencySymbol[currency] || `${currency} `;
  const formatted = amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${sym}${formatted} ${currency}`;
};

const DetailRow = ({ icon: Icon, label, value, valueNode }) => (
  <Group justify="space-between" align="center" wrap="nowrap" className={classes.detailRow}>
    <Group gap="sm" wrap="nowrap" className={classes.detailLabel}>
      {Icon ? (
        <ThemeIcon variant="light" color="violet" size={36} radius="md">
          <Icon size={18} stroke={1.75} />
        </ThemeIcon>
      ) : null}
      <Text size="sm" c="#6C94B4">
        {label}
      </Text>
    </Group>
    {valueNode ?? (
      <Text size="sm" fw={600} c="#092C4C" ta="right" className={classes.detailValue}>
        {value ?? "—"}
      </Text>
    )}
  </Group>
);

const PaymentDrawer = ({ opened, payment, onClose, onDownloadInvoice }) => {
  const isMobile = useMediaQuery("(max-width: 48em)");

  if (!payment) return null;

  const hasInvoice = Boolean(payment.invoice_path);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={isMobile ? "100%" : 440}
      withCloseButton
      title={null}
      classNames={{ body: classes.drawerBody, content: classes.drawerContent, header: classes.drawerHeader }}
    >
      <Stack gap="lg">
        <Stack gap={6}>
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap={6} wrap="nowrap" className={classes.headerId}>
              <Text fw={700} size="lg" c="#092C4C" className={classes.detailValue}>
                {payment.transaction_id || "—"}
              </Text>
              {payment.transaction_id ? (
                <CopyButton value={payment.transaction_id} timeout={2000}>
                  {({ copied, copy }) => (
                    <ActionIcon
                      variant="subtle"
                      color={copied ? "teal" : "gray"}
                      onClick={copy}
                      aria-label="Copy transaction ID"
                    >
                      <IconCopy size={16} />
                    </ActionIcon>
                  )}
                </CopyButton>
              ) : null}
            </Group>
            <StatusText status={payment.transaction_status} />
          </Group>
          <Text size="sm" c="dimmed">
            {formatPaymentDateTime(payment.created_on)}
          </Text>
        </Stack>

        <Paper className={classes.amountHero} radius="md" p="md">
          <Group gap="md" wrap="nowrap" align="center">
            <ThemeIcon className={classes.walletIcon} size={48} radius="md">
              <IconWallet size={24} stroke={1.75} />
            </ThemeIcon>
            <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
              <Text fw={700} size="xl" c="green.7" className={classes.heroAmount}>
                {formatAmountLine(payment)}
              </Text>
              <Text size="sm" c="#6C94B4">
                {paidViaLabel(payment.method)}
              </Text>
            </Stack>
          </Group>
        </Paper>

        <Stack gap="xs">
          <Text fw={600} c="#033F7C" className={classes.sectionTab}>
            Payment Details
          </Text>
          <Stack gap={0} divider={<Divider color="#E8EEF4" />}>
            <DetailRow icon={IconReceipt} label="Transaction ID" value={payment.transaction_id} />
            <DetailRow icon={IconFileInvoice} label="Invoice No." value={payment.invoice_number} />
            <DetailRow icon={IconWallet} label="Payment Method" value={formatMethodLabel(payment.method)} />
            <DetailRow
              icon={IconReceipt}
              label="Total Amount"
              valueNode={
                <Text size="sm" fw={700} c="green.7" ta="right">
                  {formatAmountLine(payment)}
                </Text>
              }
            />
            <DetailRow
              icon={IconReceipt}
              label="Status"
              valueNode={<StatusText status={payment.transaction_status} />}
            />
            <DetailRow
              icon={IconCalendar}
              label="Date & Time"
              value={formatPaymentDateTime(payment.created_on)}
            />
          </Stack>
        </Stack>

        <Paper className={classes.invoiceCard} radius="md" p="md">
          <Group justify="space-between" align="center" wrap="nowrap" gap="md">
            <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
              <ThemeIcon variant="light" color="red" size={40} radius="md">
                <IconFileInvoice size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Text fw={600} size="sm" c="#092C4C">
                  Invoice
                </Text>
                <Text size="xs" c="dimmed">
                  Download payment invoice
                </Text>
              </Stack>
            </Group>
            <Button
              variant="default"
              size="sm"
              leftSection={<IconDownload size={16} />}
              disabled={!hasInvoice}
              onClick={() => onDownloadInvoice?.(payment)}
              className={classes.downloadBtn}
            >
              Download PDF
            </Button>
          </Group>
        </Paper>
      </Stack>
    </Drawer>
  );
};

export default PaymentDrawer;
